import React, { useState, useEffect } from 'react';
import { supabase, hashPassword } from '../../services/supabase';

const AdminResetPassword = () => {
  const [sessionReady, setSessionReady] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Listen for PASSWORD_RECOVERY event from Supabase Auth
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'PASSWORD_RECOVERY') {
          setSessionReady(true);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      // Update password in Supabase Auth
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) throw updateError;

      // Get the current session to find the user
      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        // Update legacy password_hash in admin_users table for backward compatibility
        const newHash = await hashPassword(newPassword);

        const { error: legacyUpdateError } = await supabase
          .from('admin_users')
          .update({ password_hash: newHash })
          .eq('supabase_auth_id', user.id);

        if (legacyUpdateError) {
          console.warn('Failed to update legacy admin password hash:', legacyUpdateError);
        }
      }

      setSuccess(true);

      // Sign out and redirect to admin login after 2 seconds
      setTimeout(async () => {
        await supabase.auth.signOut();
        window.location.href = '/admin';
      }, 2000);

    } catch (error) {
      console.error('Password reset error:', error);
      const msg = error.message?.toLowerCase() || '';
      if (msg.includes('same') || msg.includes('different') || msg.includes('should be different')) {
        setError('Your new password must be different from your current password. Please choose a different one.');
      } else {
        setError('Failed to reset password. Please try again or request a new reset link.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!sessionReady) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '40px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
          maxWidth: '400px',
          width: '100%',
          textAlign: 'center',
          border: '2px solid #dc2626'
        }}>
          <h2 style={{ color: '#dc2626', marginBottom: '15px' }}>Invalid or Expired Link</h2>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            This password reset link is invalid or has expired. Please request a new one from the admin login page.
          </p>
          <button
            onClick={() => window.location.href = '/admin'}
            style={{
              background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 24px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 6px 20px rgba(220, 38, 38, 0.3)'
            }}
          >
            Back to Admin Login
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '40px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
          maxWidth: '400px',
          width: '100%',
          textAlign: 'center',
          border: '2px solid #28a745'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '20px' }}>✓</div>
          <h2 style={{ color: '#28a745', marginBottom: '15px' }}>Password Reset Successful!</h2>
          <p style={{ color: '#666', marginBottom: '10px' }}>
            Your admin password has been updated successfully.
          </p>
          <p style={{ color: '#888', fontSize: '0.9rem' }}>
            Redirecting to admin login...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '20px',
        padding: '40px',
        boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
        maxWidth: '400px',
        width: '100%',
        border: '2px solid #dc2626'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <img
            src="https://i.ibb.co/hF3Q8YrX/menuron-high-resolution-logo.png"
            alt="Menuron Admin"
            style={{
              height: '80px',
              marginBottom: '20px',
              filter: 'drop-shadow(0 4px 12px rgba(220, 38, 38, 0.3))'
            }}
          />
          <h1 style={{
            margin: '0 0 10px 0',
            fontSize: '1.8rem',
            fontWeight: '700',
            color: '#dc2626'
          }}>
            Set New Admin Password
          </h1>
          <p style={{ margin: '0', color: '#666', fontSize: '0.9rem' }}>
            Enter your new password below
          </p>
        </div>

        {error && (
          <div style={{
            background: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '20px',
            color: '#dc2626',
            fontSize: '0.9rem'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleResetPassword}>
          <div style={{ marginBottom: '20px' }}>
            <input
              type="password"
              placeholder="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={{
                width: '100%',
                padding: '15px 18px',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'border-color 0.3s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = '#dc2626'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>

          <div style={{ marginBottom: '25px' }}>
            <input
              type="password"
              placeholder="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={{
                width: '100%',
                padding: '15px 18px',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'border-color 0.3s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = '#dc2626'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              background: loading ? '#ccc' : 'linear-gradient(135deg, #dc2626, #b91c1c)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '15px',
              fontSize: '1.1rem',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: loading ? 'none' : '0 6px 20px rgba(220, 38, 38, 0.3)',
              transition: 'all 0.3s ease',
              marginBottom: '15px'
            }}
          >
            {loading ? 'Resetting Password...' : 'Reset Password'}
          </button>

          <button
            type="button"
            onClick={() => window.location.href = '/admin'}
            style={{
              width: '100%',
              background: 'transparent',
              color: '#666',
              border: '2px solid #e5e7eb',
              borderRadius: '12px',
              padding: '12px',
              fontSize: '0.9rem',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            Back to Admin Login
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminResetPassword;
